/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ExternalLinkIcon, EyeIcon, FileTextIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ReactTableCell as DocumentTableCell } from "@/pages/document/utils/react-table-cell";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useProcess } from "@/global-data";
import { useToast } from "@/hooks/use-toast";
import { getUserInfo } from "@/lib/token";
import { getCurrentTimeZone } from "@/lib/utils";
import { apiServices } from "@/services/api.services";
import { documents } from "@/services/global.services";

const TextWithTooltip = ({ value }: { value?: string }) => {
  if (!value) return <span>-</span>;
  return (
    <Tooltip>
      <TooltipTrigger className="text-left line-clamp-1">
        {value}
      </TooltipTrigger>
      <TooltipContent>
        <TooltipArrow />
        <p>{value}</p>
      </TooltipContent>
    </Tooltip>
  );
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd-MM-yyyy hh:mm a");
  } catch {
    return value;
  }
};

export const RowActions = ({ row }: any) => {
  const archiveId = row?.originData?.archive_id;
  const navigate = useNavigate();
  const { setProcess } = useProcess();
  const { toast } = useToast();
  const [documentList, setDocumentList] = useState<any[]>([]);

  const onViewEmail = () => {
    navigate(`/email-inbox/details/${archiveId}`);
  };

  const onViewDocument = async () => {
    const { company_id, user_id } = getUserInfo() || {};
    const payload = {
      data: {
        filter_by: {
          archive_id: archiveId,
          date_range: [],
          date_range_by: "processed_date",
          search_on: [],
          include_columns: [],
          search_text: "",
          exact_match: false,
          pagination: {
            page_no: 1,
            no_of_recs: 10,
            sort_by: "cts",
            order_by: false,
          },
        },
      },
      company_id: company_id,
      user_id: user_id || "",
      timezone: getCurrentTimeZone(),
    };

    setProcess(true);
    const { data, error, message } = await apiServices<any>(
      documents.get,
      "post",
      payload,
    );
    setProcess(false);

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
      return;
    }

    const list = (data?.result?.metadata?.[documents.key] || []).filter(
      (item: any) => item.archive_id === archiveId,
    );

    if (list.length === 0) {
      toast({
        title: "No document found",
        description: "No document has been created from this email.",
      });
      return;
    }

    setDocumentList(list);
  };

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <div className="flex items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={"ghost"}
                size={"icon"}
                className="text-primary"
                onClick={onViewEmail}
              >
                <EyeIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>View Email</p>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={"ghost"}
                size={"icon"}
                className="text-primary"
                onClick={onViewDocument}
              >
                <FileTextIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>View Document</p>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>

      <Dialog
        open={documentList.length > 0}
        onOpenChange={(open) => !open && setDocumentList([])}
      >
        <DialogContent className="max-w-6xl w-[calc(100vw-32px)] gap-3">
          <DialogHeader>
            <DialogTitle>Documents</DialogTitle>
            <DialogDescription>
              {documentList.length} document
              {documentList.length > 1 ? "s" : ""} created from this email
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60dvh] overflow-auto scrollbar border rounded-md">
            <TooltipProvider delayDuration={0}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="pl-3">Retailer Name</TableHead>
                    <TableHead>Reference #</TableHead>
                    <TableHead className="whitespace-nowrap">
                      SNC Ref #
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Processed On</TableHead>
                    <TableHead>Document Type</TableHead>
                    <TableHead className="sticky right-0 bg-white">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentList.map((item) => {
                    return (
                      <TableRow key={item.doc_id}>
                        <TableCell className="pl-3">
                          <TextWithTooltip value={item.retailer_name} />
                        </TableCell>
                        <TableCell>
                          <TextWithTooltip value={item.reference_number} />
                        </TableCell>
                        <TableCell>{item.erp_ref_number || "-"}</TableCell>
                        <TableCell>
                          <DocumentTableCell
                            header="state"
                            value={item.state}
                            row={item}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(item.uts)}
                        </TableCell>
                        <TableCell>{item.doc_type || "-"}</TableCell>
                        <TableCell className="sticky right-0 bg-white">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Link
                                to={`/document/details/${item.doc_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center rounded-md h-9 w-9 text-primary hover:bg-accent [&_svg]:size-4"
                              >
                                <ExternalLinkIcon />
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>View Document</p>
                              <TooltipArrow />
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TooltipProvider>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
